import { useCallback, useMemo, useState } from 'react'
import { useApp } from '../store/AppContext'
import { useDismiss } from '../lib/hooks'
import * as db from '../lib/db'
import { liveOfKind, type ClientKind, type ID } from '../lib/types'

/**
 * Pick a client, to narrow the project picker beside it.
 *
 * Time is tracked against a project, never a client, so this does not choose
 * what gets logged -- it filters what the project picker offers. With a
 * client per project, scrolling the whole catalogue to reach one project was
 * the thing that made the picker slow to use.
 *
 * Deliberately close to ProjectPicker in shape: the same popover, the same
 * sticky search, the same always-available create row at the bottom. Two
 * controls sitting side by side that behaved differently would be worse than
 * a little repetition.
 *
 * `kind` picks which population this instance works in -- clients or
 * prospects -- and it is required rather than defaulting to 'client', so
 * adding a picker somewhere forces the question to be answered. Everything
 * follows from it: the list, the wording, and the kind of row the create
 * button makes. The two sets are never offered together.
 */
export function ClientPicker({
  kind,
  clientId,
  onPick,
  align = 'left',
}: {
  kind: ClientKind
  clientId: ID | null
  onPick: (clientId: ID | null) => void
  align?: 'left' | 'right'
}) {
  const { ws, client, act, flash } = useApp()
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  /** null = not creating; a string = the name being typed. */
  const [name, setName] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  // Lower case for mid-sentence, capitalised for a label or a button.
  const noun = kind === 'prospect' ? 'prospect' : 'client'
  const Noun = kind === 'prospect' ? 'Prospect' : 'Client'

  const close = useCallback(() => setOpen(false), [])
  const ref = useDismiss<HTMLDivElement>(open, close)

  const selected = client(clientId)

  /*
   * This kind only, and only the ones still in use. The other kind is not
   * hidden behind a filter; it is absent. So is a client in the bin: its
   * past time still shows everywhere it was logged, but there is no
   * attaching new work to something that has been deleted.
   */
  const mine = useMemo(() => liveOfKind(ws?.clients ?? [], kind), [ws, kind])

  const rows = useMemo(() => {
    const query = q.trim().toLowerCase()
    return mine
      .filter((c) => c.status === 'active')
      .filter((c) => !query || c.name.toLowerCase().includes(query))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [mine, q])

  const typed = q.trim()
  // Only a clash within this kind counts. A prospect and a client may share a
  // name -- often the same company, before and after it said yes.
  const nameIsNew =
    typed.length > 0 &&
    !mine.some((c) => c.name.trim().toLowerCase() === typed.toLowerCase())

  const createAndPick = async () => {
    if (!ws || creating) return
    const named = (name ?? '').trim()
    if (!named) {
      flash(`Give the ${noun} a name.`)
      return
    }

    setCreating(true)
    try {
      // createClient returns the new id, so there is no waiting on a snapshot
      // and no looking the row back up by name.
      const id = await db.createClient(ws.id, {
        name: named,
        contact: '',
        isProspect: kind === 'prospect',
      })
      await act.updateWorkspace({})
      onPick(id)
      flash(`Created ${named}`)
      setName(null)
      setOpen(false)
      setQ('')
    } catch (err) {
      flash(err instanceof Error ? err.message : `Could not create the ${noun}.`)
    } finally {
      setCreating(false)
    }
  }

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        className={selected ? 'picker-btn picker-btn--set' : 'picker-btn'}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          setOpen((v) => !v)
          setQ('')
          setName(null)
        }}
      >
        <span
          className="dot"
          style={{ background: selected ? 'var(--ac)' : 'var(--line)' }}
          aria-hidden
        />
        <span className="trunc" title={selected?.name ?? Noun}>
          {selected?.name ?? Noun}
        </span>
      </button>

      {open && (
        <div
          className="popover popover--down"
          style={{ [align]: 0 } as React.CSSProperties}
          role="listbox"
        >
          <input
            autoFocus
            className="popover-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${noun}s…`}
            aria-label={`Search ${noun}s`}
          />

          <div style={{ padding: 6 }}>
            <button
              type="button"
              role="option"
              aria-selected={clientId === null}
              className="popover-row"
              onClick={() => {
                onPick(null)
                setOpen(false)
              }}
            >
              <span className="dot" style={{ background: '#CFCABC' }} aria-hidden />
              <span className="trunc" style={{ flex: 1, minWidth: 0 }}>
                All {noun}s
              </span>
            </button>

            {rows.map((c) => (
              <button
                key={c.id}
                type="button"
                role="option"
                aria-selected={c.id === clientId}
                className="popover-row"
                onClick={() => {
                  onPick(c.id)
                  setOpen(false)
                }}
              >
                <span className="dot" style={{ background: 'var(--ac)' }} aria-hidden />
                <span className="trunc" style={{ flex: 1, minWidth: 0 }} title={c.name}>
                  {c.name}
                </span>
              </button>
            ))}

            {rows.length === 0 && (
              <div style={{ padding: '10px 10px 6px', fontSize: 12.5, color: 'var(--mut)' }}>
                {q ? `No ${noun} matches that.` : `No ${noun}s yet.`}
              </div>
            )}

            {/* The form replaces the create row rather than sitting under it,
                so the sticky footer is never two things deep. */}
            {name === null ? (
              <button
                type="button"
                className="popover-row popover-create"
                title={nameIsNew ? `Create “${typed}”` : `Create a ${noun}`}
                onClick={() => setName(nameIsNew ? typed : '')}
              >
                <span
                  aria-hidden
                  style={{ width: 9, textAlign: 'center', color: 'var(--ac)', fontWeight: 700 }}
                >
                  +
                </span>
                <span className="trunc" style={{ flex: 1, minWidth: 0 }}>
                  {nameIsNew ? `Create ${noun} “${typed}”` : `New ${noun}`}
                </span>
              </button>
            ) : (
              <div
                className="popover-create"
                style={{
                  borderTop: '1px solid var(--line)',
                  marginTop: 4,
                  paddingTop: 10,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                <input
                  autoFocus
                  className="input"
                  value={name}
                  placeholder={`${Noun} name`}
                  aria-label={`New ${noun} name`}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void createAndPick()
                    else if (e.key === 'Escape') setName(null)
                  }}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    className="btn btn--primary"
                    style={{ flex: 1 }}
                    disabled={creating || !name.trim()}
                    onClick={() => void createAndPick()}
                  >
                    {creating ? 'Creating…' : `Create ${noun}`}
                  </button>
                  <button type="button" className="btn" onClick={() => setName(null)}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
